import { type GetPortfolioLabChartItem, PortfolioLabContext } from "$root/functional-areas/portfolio-lab/ctx";
import { useLabSession } from "$root/functional-areas/portfolio-lab/hooks/useLabSession";
import { writeAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { Column, Text } from "@mdotm/shadcn/legacy/components";
import type { ColumnState } from "ag-grid-community";
import { useMemo } from "react";
import { ColumnSettingsPanel } from "../shared/ColumnSettingsPanel";

type PortfolioMetricsSettingsProps = {
	/** uuid of the TABLE chart this panel manages — there can be more than one. */
	chartUuid: string;
};

export function PortfolioMetricsSettings({ chartUuid }: PortfolioMetricsSettingsProps) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const { saveLabSession } = useLabSession();

	const charts = useAtomValue(ctx.charts$);
	const parametrizedConcepts = useAtomValue(ctx.parametrizedConcepts$);

	// Source of truth: THIS TABLE chart's persisted column state (order + visibility).
	const columnStates: ColumnState[] = useMemo(
		() =>
			charts.find((c): c is GetPortfolioLabChartItem<"TABLE"> => c.type === "TABLE" && c.uuid === chartUuid)?.preference
				.columns ?? [],
		[charts, chartUuid],
	);

	const columnState = useMemo(
		() =>
			columnStates.map((col) => {
				const pc = parametrizedConcepts.find((c) => c.uuid === col.colId);
				return { colId: col.colId, label: pc?.label ?? col.colId, hide: col.hide ?? false };
			}),
		[columnStates, parametrizedConcepts],
	);

	function handleChange(newColumns: { colId: string; hide: boolean }[]) {
		const colMap = new Map(columnStates.map((c) => [c.colId, c]));
		const newState = newColumns.map((item) => {
			const existing = colMap.get(item.colId);
			return existing ? { ...existing, hide: item.hide } : { colId: item.colId, hide: item.hide };
		});
		writeAtom(ctx.charts$, (cs) =>
			cs.map((c) =>
				c.type === "TABLE" && c.uuid === chartUuid ? { ...c, preference: { ...c.preference, columns: newState } } : c,
			),
		);
		saveLabSession();
	}

	return (
		<ColumnSettingsPanel
			title="Portfolio Metrics"
			description="Manage metrics display options"
			columnState={columnState}
			onChange={handleChange}
			emptyState={
				<Column className="items-center justify-center h-full">
					<Text type="Body/L/Book">No metrics available yet.</Text>
				</Column>
			}
		/>
	);
}
